import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapPin, MessageSquare, ArrowLeft, Truck, ExternalLink, Copy, Instagram, Mail, Phone, X, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { accentColor } from "@/lib/theme";
import { useCart } from "@/hooks/useCart";
import { CartDrawer } from "@/components/CartDrawer";

const money = (c) => `$${(c / 100).toFixed(2)}`;

function PayLinks({ seller, product }) {
  const amount = product.price_cents / 100;
  const note = encodeURIComponent(`Cake Pop Talk: ${product.title}`);
  const buttons = [];
  if (seller.payment_cashapp) {
    const tag = seller.payment_cashapp.replace(/^\$/, "");
    buttons.push({ key: "cashapp", label: `Cash App $${tag}`, href: `https://cash.app/$${tag}${amount > 0 ? `/${amount}` : ""}`, color: "#00D64F" });
  }
  if (seller.payment_venmo) {
    const handle = seller.payment_venmo.replace(/^@/, "");
    buttons.push({ key: "venmo", label: `Venmo @${handle}`, href: `https://venmo.com/${handle}?txn=pay${amount > 0 ? `&amount=${amount}` : ""}&note=${note}`, color: "#008CFF" });
  }
  if (seller.payment_paypal) {
    const handle = seller.payment_paypal.replace(/^https?:\/\/(www\.)?paypal\.me\//, "").replace(/^@/, "");
    buttons.push({ key: "paypal", label: `PayPal.me/${handle}`, href: `https://paypal.me/${handle}${amount > 0 ? `/${amount}USD` : ""}`, color: "#003087" });
  }
  if (seller.payment_zelle) {
    buttons.push({ key: "zelle", label: `Zelle: ${seller.payment_zelle}`, copy: seller.payment_zelle, color: "#6D1ED4" });
  }
  if (buttons.length === 0) return null;
  return (
    <div className="mt-4 space-y-2" data-testid="pay-links">
      <div className="text-xs uppercase tracking-widest text-[#857163]">Seller accepts</div>
      {buttons.map((b) => (
        b.href ? (
          <a
            key={b.key}
            href={b.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-2 w-full px-4 py-3 rounded-2xl text-white font-semibold text-sm transition hover:opacity-90"
            style={{ background: b.color }}
            data-testid={`pay-btn-${b.key}`}
          >
            <span>{b.label}</span>
            <ExternalLink className="w-4 h-4 opacity-80" />
          </a>
        ) : (
          <button
            key={b.key}
            onClick={() => { navigator.clipboard.writeText(b.copy); toast.success("Zelle contact copied"); }}
            className="flex items-center justify-between gap-2 w-full px-4 py-3 rounded-2xl text-white font-semibold text-sm hover:opacity-90 transition"
            style={{ background: b.color }}
            data-testid={`pay-btn-${b.key}`}
          >
            <span>{b.label}</span>
            <Copy className="w-4 h-4 opacity-80" />
          </button>
        )
      ))}
      {seller.payment_notes && (
        <div className="text-xs text-[#857163] mt-2 italic whitespace-pre-wrap">{seller.payment_notes}</div>
      )}
    </div>
  );
}

function ContactSellerModal({ seller, product, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState(`Hi ${seller.business_name || "@" + seller.username},\n\nI'd like to order "${product.title}" from your Cake Pop Talk shop. Could you share availability and next steps?\n\nThanks!`);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const n = name.trim();
    const e = email.trim().toLowerCase();
    const m = message.trim();
    if (!n) { toast.error("What's your name?"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Enter a valid email"); return; }
    if (m.length < 5) { toast.error("Add a short message"); return; }
    setSubmitting(true);
    try {
      await api.post("/marketplace/map/contact", { baker_id: seller.user_id, name: n, email: e, message: m });
      toast.success("Message sent — the seller will reply to your email.");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send the message");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/40 p-4" data-testid="storefront-contact-modal" onClick={() => !submitting && onClose()}>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#8EAC50]" />
            <div className="font-heading text-xl text-[#3B2C24]">Message {seller.business_name || "@" + seller.username}</div>
          </div>
          <button onClick={onClose} disabled={submitting} className="text-[#857163] hover:text-[#3B2C24] disabled:opacity-50" data-testid="storefront-contact-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-sm text-[#857163] mt-1 mb-4">
          We'll email your message to the seller. They'll reply directly to the email address you provide.
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs text-[#857163]">Your name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className="cp-input mt-1" placeholder="Jane Baker" data-testid="storefront-contact-name" />
          </label>
          <label className="block">
            <span className="text-xs text-[#857163]">Your email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="cp-input mt-1" placeholder="you@example.com" data-testid="storefront-contact-email" />
          </label>
          <label className="block">
            <span className="text-xs text-[#857163]">Message</span>
            <textarea value={message} onChange={(e) => setMessage(e.target.value.slice(0, 1000))} rows={5} className="cp-input mt-1" data-testid="storefront-contact-message" />
            <div className="text-right text-[10px] text-[#857163] mt-0.5">{1000 - message.length} chars left</div>
          </label>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} disabled={submitting} className="flex-1 px-4 py-2 rounded-full text-sm border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6] disabled:opacity-50" data-testid="storefront-contact-cancel">Cancel</button>
          <button onClick={submit} disabled={submitting} className="flex-1 cp-btn-primary disabled:opacity-60" data-testid="storefront-contact-submit">
            {submitting ? "Sending…" : "Send message"}
          </button>
        </div>
        <div className="mt-3 text-center text-[10px] text-[#857163]">
          Site members can also <Link to="/login" className="underline">sign in</Link> to message this seller in-app.
        </div>
      </div>
    </div>
  );
}


function ContactLinks({ seller, product, onOpenContact }) {
  const items = [];
  if (seller.contact_instagram) {
    const handle = seller.contact_instagram.replace(/^@/, "");
    items.push({ key: "ig", label: `Instagram @${handle}`, href: `https://instagram.com/${handle}`, icon: Instagram, color: "#E1306C" });
  }
  if (seller.contact_phone) {
    const raw = seller.contact_phone.replace(/[^\d+]/g, "");
    items.push({ key: "phone", label: seller.contact_phone, href: `sms:${raw}`, icon: Phone, color: "#8EAC50" });
  }
  // Always show a form-based "Contact seller" button (never expose the raw email) if the seller accepts contact.
  const hasContact = !!(seller.contact_email || items.length > 0);
  if (!hasContact) return null;
  return (
    <div className="mt-4 space-y-2" data-testid="contact-links">
      <div className="text-xs uppercase tracking-widest text-[#857163]">Contact seller directly</div>
      {seller.contact_email && (
        <button
          type="button"
          onClick={onOpenContact}
          className="flex items-center justify-between gap-2 w-full px-4 py-3 rounded-2xl text-white font-semibold text-sm transition hover:opacity-90 bg-[#3B2C24]"
          data-testid="contact-btn-email"
        >
          <span className="inline-flex items-center gap-2"><Mail className="w-4 h-4" /> Send seller a message</span>
          <MessageSquare className="w-4 h-4 opacity-80" />
        </button>
      )}
      {items.map((it) => (
        <a
          key={it.key}
          href={it.href}
          target={it.key === "phone" ? undefined : "_blank"}
          rel="noopener noreferrer"
          className="flex items-center justify-between gap-2 w-full px-4 py-3 rounded-2xl text-white font-semibold text-sm transition hover:opacity-90"
          style={{ background: it.color }}
          data-testid={`contact-btn-${it.key}`}
        >
          <span className="inline-flex items-center gap-2"><it.icon className="w-4 h-4" /> {it.label}</span>
          <ExternalLink className="w-4 h-4 opacity-80" />
        </a>
      ))}
    </div>
  );
}

export default function Product() {
  const { username, slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [showContact, setShowContact] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const cart = useCart(username);

  useEffect(() => {
    api.get(`/shop/${username}/products/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(false))
      .finally(() => setLoading(false));
  }, [username, slug]);

  if (loading) return (<div className="p-8 text-[#857163]">Loading…</div>);
  if (data === false) return (<div className="p-8 text-[#857163]">Product not found</div>);

  const p = data.product;
  const s = data.seller;
  const accent = accentColor(s);
  const cover = p.images?.[activeImg] || p.images?.[0];
  const msgSeller = () => {
    if (!user || user === false) { nav("/login"); return; }
    nav(`/messages/${s.user_id}`);
  };
  const isMyShop = user && user !== false && user.id === s.user_id;
  const isCustomOnly = p.is_custom_order && p.price_cents === 0;
  const soldOut = !!p.sold_out;
  const canAddToCart = !isMyShop && !isCustomOnly && !soldOut;
  const effectiveUnitPrice = p.discount_pct
    ? Math.round(p.price_cents * (100 - p.discount_pct) / 100)
    : p.discount_cents
      ? Math.max(0, p.price_cents - p.discount_cents)
      : p.price_cents;
  const addToCart = () => {
    cart.addItem({
      product_id: p.id,
      title: p.title,
      image_url: p.images?.[0] || null,
      unit_price_cents: effectiveUnitPrice,
      quantity: 1,
    });
    toast.success(`${p.title} added to cart`);
  };

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 lg:py-10" style={{ ["--shop-accent"]: accent }}>
        <button onClick={() => nav(-1)} className="cp-btn-ghost inline-flex items-center gap-1.5 mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
          <div>
            <div className="aspect-[4/3] rounded-3xl overflow-hidden border border-[#EBDCD1] bg-[#F9EFE6]">
              {cover ? <img src={fileUrl(cover)} alt="" className="w-full h-full object-cover" /> :
                <div className="w-full h-full grid place-items-center text-[#857163]">No image</div>}
            </div>
            {p.images?.length > 1 && (
              <div className="mt-3 flex gap-2 flex-wrap">
                {p.images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className="w-16 h-16 rounded-xl overflow-hidden border-2 transition" style={{ borderColor: i === activeImg ? accent : "#EBDCD1" }}>
                    <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Link to={`/shop/${s.username}`} className="text-sm text-[#857163] flex items-center gap-1.5 mb-1 transition" style={{}} onMouseEnter={(e) => { e.currentTarget.style.color = accent; }} onMouseLeave={(e) => { e.currentTarget.style.color = ""; }}>
              <span>{s.business_name}</span>
              {s.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.city}</span>}
            </Link>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#3B2C24] leading-tight">{p.title}</h1>
            <div className="mt-3 flex items-center gap-3">
              <div className="font-heading text-3xl font-bold" style={{ color: accent }}>
                {p.is_custom_order && p.price_cents === 0 ? "Custom quote" : money(p.price_cents)}
              </div>
              {p.is_custom_order && <span className="cp-tag" style={{ background: `${accent}26`, color: accent }}>Custom orders</span>}
            </div>

            <div className="mt-4 flex flex-wrap gap-2 text-sm">
              {p.fulfillment_pickup && <span className="cp-tag" style={{ background: "rgba(142,172,80,0.15)" }}><MapPin className="w-3 h-3" /> Local pickup</span>}
              {p.fulfillment_shipping && <span className="cp-tag" style={{ background: "rgba(255,208,127,0.28)" }}><Truck className="w-3 h-3" /> Ships</span>}
              {p.lead_time_days ? <span className="cp-tag">Lead time: {p.lead_time_days}d</span> : null}
              {typeof p.available_quantity === "number" && !soldOut && (
                <span
                  className="cp-tag font-semibold"
                  style={{
                    background: p.low_stock_threshold != null && p.available_quantity <= p.low_stock_threshold
                      ? "rgba(244,168,59,0.22)"
                      : "rgba(142,172,80,0.15)",
                    color: p.low_stock_threshold != null && p.available_quantity <= p.low_stock_threshold
                      ? "#B26A00"
                      : "#4C7A2E",
                  }}
                  data-testid="product-stock-pill"
                >
                  {p.available_quantity} in stock{p.low_stock_threshold != null && p.available_quantity <= p.low_stock_threshold ? " — order soon!" : ""}
                </span>
              )}
            </div>

            <p className="mt-5 text-[#3B2C24] whitespace-pre-wrap leading-relaxed">{p.description}</p>

            {p.allergens && (
              <div className="mt-4 p-3 rounded-2xl bg-[#F9EFE6]/60 border border-[#EBDCD1] text-sm">
                <span className="font-semibold text-[#3B2C24]">Allergens: </span>
                <span className="text-[#857163]">{p.allergens}</span>
              </div>
            )}

            {canAddToCart && (
              <div className="mt-6 flex gap-2" data-testid="product-cart-actions">
                <button
                  onClick={addToCart}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full text-white font-semibold text-sm hover:opacity-90 transition"
                  style={{ background: accent }}
                  data-testid="product-add-to-cart-btn"
                >
                  <ShoppingCart className="w-4 h-4" /> Add to cart
                </button>
                <button
                  onClick={() => setShowCart(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full border-2 font-semibold text-sm hover:bg-[#FFF9EF] transition relative"
                  style={{ borderColor: accent, color: accent }}
                  data-testid="product-view-cart-btn"
                >
                  Cart
                  {cart.itemCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold text-white" style={{ background: accent }} data-testid="product-cart-badge">{cart.itemCount}</span>
                  )}
                </button>
              </div>
            )}

            <div className="mt-6 space-y-3">
              {(() => {
                const isGuest = !user || user === false;
                const hasExternal = !!(s.contact_instagram || s.contact_email || s.contact_phone);
                if (isGuest && hasExternal) {
                  return (
                    <>
                      <ContactLinks seller={s} product={p} onOpenContact={() => setShowContact(true)} />
                      <div className="text-center text-xs text-[#857163] pt-1">
                        Prefer to keep it on Cake Pop Talk?{" "}
                        <Link to="/login" className="text-[#FF7E67] font-medium hover:underline">Log in</Link>
                        {" "}or{" "}
                        <Link to="/register" className="text-[#FF7E67] font-medium hover:underline">create a free account</Link>
                        {" "}to message the seller in-app.
                      </div>
                    </>
                  );
                }
                return (
                  <>
                    <button onClick={msgSeller} className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full text-white font-semibold text-sm hover:opacity-90 transition" style={{ background: accent }} data-testid="message-seller-btn">
                      <MessageSquare className="w-4 h-4" />
                      {isGuest ? "Log in to message seller" : "Message seller to order"}
                    </button>
                    {isGuest && (
                      <div className="text-center text-xs text-[#857163]">
                        Or use the payment options below to order as a guest.
                      </div>
                    )}
                    <ContactLinks seller={s} product={p} onOpenContact={() => setShowContact(true)} />
                  </>
                );
              })()}
              <PayLinks seller={s} product={p} />
            </div>
          </div>
        </div>
        {showContact && (
          <ContactSellerModal seller={s} product={p} onClose={() => setShowContact(false)} />
        )}
        <CartDrawer
          username={s.username}
          businessName={s.business_name}
          accent={accent}
          open={showCart}
          onClose={() => setShowCart(false)}
        />
      </main>
  );
}
